/**
 * Tabs — Base UI primitive with this workspace's underline treatment.
 *
 * The active tab is marked by a moving indicator rather than a filled pill, so
 * a row of seven tabs stays quiet enough to sit under a page title. The quiet
 * variant heads a list inside a page (tab-bar.md): smaller, no weight change,
 * and the underline fades in place instead of moving.
 *
 * @module components/ui/tabs
 */
import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";

import { cn } from "~/lib/utils";

export function Tabs({ className, ...props }: ComponentProps<typeof BaseTabs.Root>) {
  return <BaseTabs.Root className={cn("flex min-h-0 flex-col", className)} {...props} />;
}

const tabsListVariants = cva("relative flex shrink-0 items-center gap-1 overflow-x-auto", {
  defaultVariants: { variant: "default" },
  variants: {
    variant: {
      default: "border-b border-border/60",
      // The caller draws the rule, so a trailing link can sit on it beside the tabs.
      quiet: "min-w-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
    },
  },
});

export function TabsList({
  className,
  variant,
  ...props
}: ComponentProps<typeof BaseTabs.List> & VariantProps<typeof tabsListVariants>) {
  return <BaseTabs.List className={cn(tabsListVariants({ variant }), className)} {...props} />;
}

const tabsTabVariants = cva("relative shrink-0 cursor-pointer whitespace-nowrap text-muted-foreground", {
  defaultVariants: { variant: "default" },
  variants: {
    variant: {
      default: [
        // -mb-px pulls the tab's own border onto the list's, so the selected
        // tab sits on the line rather than above it.
        "-mb-px rounded-t-md",
        "border-b-2 border-transparent px-3 pb-2.5 pt-2",
        "text-[15px] leading-6",
        "transition-colors duration-150 motion-reduce:transition-none",
        "hover:bg-accent/30 hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40",
        // The selected tab must be unmistakable at a glance: ink weight and a
        // full-strength underline, not just a hue shift. Base UI marks the
        // active Tab with data-active (see TabsTabDataAttributes) — styling
        // data-selected here silently styles nothing.
        "data-[active]:border-primary data-[active]:font-semibold data-[active]:text-foreground",
        "aria-selected:border-primary aria-selected:font-semibold aria-selected:text-foreground",
      ],
      quiet: [
        "flex h-8 items-center gap-1.5 rounded-md px-2 text-[14px] leading-5 outline-none",
        "transition-[background-color] duration-[120ms] ease-out hover:bg-accent/70",
        "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70",
        "aria-selected:text-foreground",
        // The underline spans the label and count, so the first one starts on the column's edge with its label.
        "after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:bg-foreground after:opacity-0",
        "after:transition-opacity after:duration-[120ms] after:ease-out motion-reduce:after:transition-none aria-selected:after:opacity-100",
      ],
    },
  },
});

export function TabsTab({
  className,
  variant,
  ...props
}: ComponentProps<typeof BaseTabs.Tab> & VariantProps<typeof tabsTabVariants>) {
  return <BaseTabs.Tab className={cn(tabsTabVariants({ variant }), className)} {...props} />;
}

export function TabsPanel({ className, ...props }: ComponentProps<typeof BaseTabs.Panel>) {
  return <BaseTabs.Panel className={cn("min-h-0 flex-1", className)} {...props} />;
}
